import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Divider,
  DatePicker,
  Upload,
  Tag,
  Modal,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  DollarOutlined,
  BankOutlined,
  FileTextOutlined,
  GlobalOutlined,
  CalendarOutlined,
  UploadOutlined,
  SaveOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface CompanySettings {
  companyName: string;
  address: string;
  taxId: string;
  currency: string;
  fiscalYearStart: string;
  accountingMethod: 'accrual' | 'cash';
  multiCurrency: boolean;
}

interface TaxSetting {
  id: string;
  name: string;
  rate: number;
  type: 'sales' | 'purchase' | 'both';
  account: string;
  isActive: boolean;
}

interface PaymentTerm {
  id: string;
  name: string;
  days: number;
  type: 'net' | 'eom' | 'discount';
  discountPercent?: number;
  discountDays?: number;
  isActive: boolean;
}

interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  exchangeRate: number;
  isBase: boolean;
  isActive: boolean;
}

const AccountingSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'tax' | 'payment-term' | 'currency'>('tax');
  const [companyForm] = Form.useForm();
  const [modalForm] = Form.useForm();

  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    companyName: 'Wyni AI ERP Inc.',
    address: '123 Business St, City, State 12345',
    taxId: 'TAX123456789',
    currency: 'USD',
    fiscalYearStart: dayjs().startOf('year').format('YYYY-MM-DD'),
    accountingMethod: 'accrual',
    multiCurrency: false
  });

  const sampleTaxSettings: TaxSetting[] = [
    {
      id: '1',
      name: 'Sales Tax',
      rate: 8.5,
      type: 'sales',
      account: 'Sales Tax Payable',
      isActive: true
    },
    {
      id: '2',
      name: 'VAT',
      rate: 20,
      type: 'both',
      account: 'VAT Account',
      isActive: true
    }
  ];

  const samplePaymentTerms: PaymentTerm[] = [
    {
      id: '1',
      name: 'Net 30',
      days: 30,
      type: 'net',
      isActive: true
    },
    {
      id: '2',
      name: '2/10 Net 30',
      days: 30,
      type: 'discount',
      discountPercent: 2,
      discountDays: 10,
      isActive: true
    }
  ];

  const sampleCurrencies: Currency[] = [
    {
      id: '1',
      code: 'USD',
      name: 'US Dollar',
      symbol: '$',
      exchangeRate: 1.0,
      isBase: true,
      isActive: true
    },
    {
      id: '2',
      code: 'EUR',
      name: 'Euro',
      symbol: '€',
      exchangeRate: 0.85,
      isBase: false,
      isActive: true
    }
  ];

  const handleSaveCompanySettings = async (values: CompanySettings) => {
    try {
      setLoading(true);
      // API call would go here
      setCompanySettings(values);
      message.success('Company settings saved successfully');
    } catch (error) {
      message.error('Failed to save company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (type: typeof modalType) => {
    setModalType(type);
    setIsModalVisible(true);
    modalForm.resetFields();
  };

  const taxColumns: ColumnsType<TaxSetting> = [
    {
      title: 'Tax Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Rate (%)',
      dataIndex: 'rate',
      key: 'rate',
      render: (rate) => `${rate}%`
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'sales' ? 'green' : type === 'purchase' ? 'blue' : 'purple'}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="text" icon={<EditOutlined />} />
          <Button type="text" icon={<DeleteOutlined />} danger />
        </Space>
      )
    }
  ];

  const paymentTermColumns: ColumnsType<PaymentTerm> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'net' ? 'blue' : type === 'eom' ? 'green' : 'orange'}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Discount',
      key: 'discount',
      render: (_, record) => {
        if (record.type === 'discount') {
          return `${record.discountPercent}% in ${record.discountDays} days`;
        }
        return '-';
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="text" icon={<EditOutlined />} />
          <Button type="text" icon={<DeleteOutlined />} danger />
        </Space>
      )
    }
  ];

  const currencyColumns: ColumnsType<Currency> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol'
    },
    {
      title: 'Exchange Rate',
      dataIndex: 'exchangeRate',
      key: 'exchangeRate',
      render: (rate, record) => record.isBase ? 'Base Currency' : rate.toFixed(4)
    },
    {
      title: 'Type',
      dataIndex: 'isBase',
      key: 'isBase',
      render: (isBase) => (
        <Tag color={isBase ? 'gold' : 'blue'}>
          {isBase ? 'Base' : 'Foreign'}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="text" icon={<EditOutlined />} />
          <Button type="text" icon={<DeleteOutlined />} danger />
        </Space>
      )
    }
  ];

  const renderCompanyTab = () => (
    <Card title="Company Information">
      <Form
        form={companyForm}
        layout="vertical"
        initialValues={companySettings}
        onFinish={handleSaveCompanySettings}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="companyName"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="taxId"
              label="Tax ID"
              rules={[{ required: true, message: 'Please enter tax ID' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true, message: 'Please enter company address' }]}
            >
              <TextArea rows={3} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="currency"
              label="Base Currency"
              rules={[{ required: true, message: 'Please select base currency' }]}
            >
              <Select>
                <Option value="USD">USD - US Dollar</Option>
                <Option value="EUR">EUR - Euro</Option>
                <Option value="GBP">GBP - British Pound</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="fiscalYearStart"
              label="Fiscal Year Start"
              rules={[{ required: true, message: 'Please select fiscal year start' }]}
            >
              <DatePicker style={{ width: '100%' }} picker="date" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="accountingMethod"
              label="Accounting Method"
              rules={[{ required: true, message: 'Please select accounting method' }]}
            >
              <Select>
                <Option value="accrual">Accrual</Option>
                <Option value="cash">Cash</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="multiCurrency" label="Multi-Currency" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
            Save Settings
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  const renderTaxTab = () => (
    <Card
      title="Tax Settings"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddItem('tax')}>
          Add Tax
        </Button>
      }
    >
      <Table
        columns={taxColumns}
        dataSource={sampleTaxSettings}
        rowKey="id"
      />
    </Card>
  );

  const renderPaymentTermsTab = () => (
    <Card
      title="Payment Terms"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddItem('payment-term')}>
          Add Payment Term
        </Button>
      }
    >
      <Table
        columns={paymentTermColumns}
        dataSource={samplePaymentTerms}
        rowKey="id"
      />
    </Card>
  );

  const renderCurrencyTab = () => (
    <Card
      title="Currency Settings"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddItem('currency')}>
          Add Currency
        </Button>
      }
    >
      <Table
        columns={currencyColumns}
        dataSource={sampleCurrencies}
        rowKey="id"
      />
    </Card>
  );

  const renderPreferencesTab = () => (
    <Card title="Accounting Preferences">
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Default Account Numbering">
              <Select defaultValue="automatic">
                <Option value="automatic">Automatic</Option>
                <Option value="manual">Manual</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Invoice Numbering Format">
              <Input placeholder="INV-{YYYY}-{MM}-{####}" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Journal Entry Numbering">
              <Input placeholder="JE-{####}" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Payment Numbering">
              <Input placeholder="PAY-{####}" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Divider />
            <Title level={5}>Reporting Preferences</Title>
          </Col>
          <Col span={12}>
            <Form.Item label="Default Report Period">
              <Select defaultValue="monthly">
                <Option value="monthly">Monthly</Option>
                <Option value="quarterly">Quarterly</Option>
                <Option value="yearly">Yearly</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Date Format">
              <Select defaultValue="MM/DD/YYYY">
                <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Accounting Settings</Title>
        <Text type="secondary">
          Configure accounting settings, taxes, currencies, and preferences
        </Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Company" key="company" icon={<BankOutlined />}>
          {renderCompanyTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Tax Settings" key="tax" icon={<DollarOutlined />}>
          {renderTaxTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Payment Terms" key="payment-terms" icon={<CalendarOutlined />}>
          {renderPaymentTermsTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Currencies" key="currency" icon={<GlobalOutlined />}>
          {renderCurrencyTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Preferences" key="preferences" icon={<SettingOutlined />}>
          {renderPreferencesTab()}
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title={`Add ${modalType === 'tax' ? 'Tax Setting' : modalType === 'payment-term' ? 'Payment Term' : 'Currency'}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={modalForm} layout="vertical">
          {modalType === 'tax' && (
            <>
              <Form.Item name="name" label="Tax Name" rules={[{ required: true }]}>
                <Input placeholder="Sales Tax" />
              </Form.Item>
              <Form.Item name="rate" label="Tax Rate %" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
              <Form.Item name="type" label="Tax Type" rules={[{ required: true }]}>
                <Select>
                  <Option value="sales">Sales</Option>
                  <Option value="purchase">Purchase</Option>
                  <Option value="both">Both</Option>
                </Select>
              </Form.Item>
              <Form.Item name="account" label="Tax Account" rules={[{ required: true }]}>
                <Select placeholder="Select account">
                  <Option value="tax-payable">Tax Payable</Option>
                  <Option value="tax-receivable">Tax Receivable</Option>
                </Select>
              </Form.Item>
            </>
          )}
          
          {modalType === 'payment-term' && (
            <>
              <Form.Item name="name" label="Term Name" rules={[{ required: true }]}>
                <Input placeholder="Net 30" />
              </Form.Item>
              <Form.Item name="days" label="Days" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select>
                  <Option value="net">Net</Option>
                  <Option value="eom">End of Month</Option>
                  <Option value="discount">Discount</Option>
                </Select>
              </Form.Item>
            </>
          )}
          
          {modalType === 'currency' && (
            <>
              <Form.Item name="code" label="Currency Code" rules={[{ required: true }]}>
                <Input placeholder="USD" />
              </Form.Item>
              <Form.Item name="name" label="Currency Name" rules={[{ required: true }]}>
                <Input placeholder="US Dollar" />
              </Form.Item>
              <Form.Item name="symbol" label="Symbol" rules={[{ required: true }]}>
                <Input placeholder="$" />
              </Form.Item>
              <Form.Item name="exchangeRate" label="Exchange Rate" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.0001} />
              </Form.Item>
            </>
          )}
          
          <Form.Item>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Add</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountingSettings;
